import { Capacitor } from "@capacitor/core";
import {
  APPLE_SERVICES_ID,
  APPLE_REDIRECT_URI,
  APPLE_NATIVE_CLIENT_ID,
} from "@/config/oauth";

// Whether to show the "Sign in with Apple" button on this platform.
// - Native iOS: always (required by Guideline 4.8 and natively supported).
// - Web: only when a Services ID is configured for the web flow.
// - Native Android: not needed.
export function isAppleSignInAvailable() {
  const platform = Capacitor.getPlatform();
  if (platform === "ios") return true;
  if (platform === "web") return !!APPLE_SERVICES_ID;
  return false;
}

// Returns { identity_token, first_name, last_name } for the backend.
// Apple only returns the user's name on the FIRST authorization, so
// first_name/last_name may be empty on subsequent sign-ins.
export async function signInWithApple() {
  return Capacitor.getPlatform() === "ios" ? signInNative() : signInWeb();
}

async function signInNative() {
  const { SignInWithApple } = await import("@capacitor-community/apple-sign-in");
  const result = await SignInWithApple.authorize({
    clientId: APPLE_NATIVE_CLIENT_ID,
    redirectURI: APPLE_REDIRECT_URI || "https://siftcareers.com/auth/login",
    scopes: "email name",
  });
  const r = result?.response || {};
  if (!r.identityToken) {
    throw new Error("Apple did not return an identity token.");
  }
  return {
    identity_token: r.identityToken,
    first_name: r.givenName || "",
    last_name: r.familyName || "",
  };
}

async function signInWeb() {
  if (!APPLE_SERVICES_ID) {
    throw new Error("Apple sign-in is not configured for web.");
  }
  await loadAppleJs();
  window.AppleID.auth.init({
    clientId: APPLE_SERVICES_ID,
    scope: "name email",
    redirectURI: APPLE_REDIRECT_URI,
    usePopup: true,
  });
  const data = await window.AppleID.auth.signIn();
  const idToken = data?.authorization?.id_token;
  if (!idToken) {
    throw new Error("Apple did not return an identity token.");
  }
  const name = data?.user?.name || {};
  return {
    identity_token: idToken,
    first_name: name.firstName || "",
    last_name: name.lastName || "",
  };
}

let appleJsPromise = null;
function loadAppleJs() {
  if (window.AppleID) return Promise.resolve();
  if (appleJsPromise) return appleJsPromise;
  appleJsPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src =
      "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => {
      appleJsPromise = null;
      reject(new Error("Failed to load Apple sign-in."));
    };
    document.body.appendChild(s);
  });
  return appleJsPromise;
}
